import { allowedLocales } from "scripts/utils/constants";

(function(){
  const CONFIG = {
    endpoint: '/api/log',
    maxErrorsPerPage: 10
  };

  let initialized = false;
  let sentErrorCount = 0;
  let originalConsoleError = null;
  let clarityIdentifyDepth = 0;
  const pendingReports = new Set();
  let lang = document.documentElement.lang || 'en';
  if (!(lang in allowedLocales)) {
    lang = allowedLocales.uk;
  }

  function stringifyValue(value) {
    if (value instanceof Error) return value.message || String(value);
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }

  function getFirstErrorStack(args) {
    for (let i = 0; i < args.length; i++) {
      if (args[i] instanceof Error && args[i].stack) return String(args[i].stack);
    }
    return null;
  }

  function isElement(node) {
    return node && node.nodeType === 1;
  }

  function generateId() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  function getClarityIds() {
    let clarityUserId = window.localStorage.getItem('clarityUserId');
    if (!clarityUserId) {
      clarityUserId = generateId();
      window.localStorage.setItem('clarityUserId', clarityUserId);
    }

    let claritySessionId = window.sessionStorage.getItem('claritySessionId');
    if (!claritySessionId) {
      claritySessionId = generateId();
      window.sessionStorage.setItem('claritySessionId', claritySessionId);
    }

    return { clarityUserId, claritySessionId };
  }

  async function addClarityIds(errorBody) {
    if (typeof window.clarity !== 'function') return errorBody;

    clarityIdentifyDepth += 1;
    try {
      const { clarityUserId, claritySessionId } = getClarityIds();
      const identifyResult = await window.clarity('identify', clarityUserId, claritySessionId);
      errorBody.clarityUserId = identifyResult && identifyResult.userId ? identifyResult.userId : null;
      errorBody.claritySessionId = claritySessionId;
    } catch (e) {
      console.log('[logger] Clarity identify failed; sending error without Clarity ids', e);
    } finally {
      clarityIdentifyDepth -= 1;
    }
    return errorBody;
  }

  function send(body) {
    const json = JSON.stringify(body);

    if (document.visibilityState === 'hidden' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([json], { type: 'application/json' });
        if (navigator.sendBeacon(CONFIG.endpoint, blob)) return;
      } catch (e) {
        console.log('[logger] sendBeacon failed, falling back to fetch', e);
      }
    }

    fetch(CONFIG.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Accept-Language': lang,
        'x-requested-with': 'XMLHttpRequest'
      },
      body: json,
      keepalive: true
    }).catch(function (e) {
      console.log('[logger] failed to deliver error log, dropping it', e);
    });
  }

  async function report(errorBody) {
    if (!initialized) {
      return;
    }
    const { maxErrorsPerPage } = CONFIG;
    if (maxErrorsPerPage > 0 && sentErrorCount >= maxErrorsPerPage) {
      return;
    }
    sentErrorCount += 1;
    if (document.visibilityState === 'hidden') {
      send(errorBody);
      return;
    }

    const entry = { body: errorBody, sent: false };
    pendingReports.add(entry);
    try {
      const enriched = await addClarityIds(errorBody);
      if (!entry.sent) {
        entry.sent = true;
        send(enriched);
      }
    } catch (e) {
      console.log('[logger] internal error while reporting, skipped this entry', e);
    } finally {
      pendingReports.delete(entry);
    }
  }

  function flushPendingReports() {
    if (typeof navigator.sendBeacon !== 'function') {
      pendingReports.clear();
      return;
    }
    pendingReports.forEach(function (entry) {
      if (!entry.sent) {
        entry.sent = true;
        try {
          const blob = new Blob([JSON.stringify(entry.body)], { type: 'application/json' });
          navigator.sendBeacon(CONFIG.endpoint, blob);
        } catch (e) {
          console.log('[logger] flush sendBeacon failed, entry dropped', e);
        }
      }
    });
    pendingReports.clear();
  }

  function onError(event) {
    if (isElement(event.target)) {
      const el = event.target;
      const srcAttr = el.getAttribute('src');
      if (srcAttr !== null && srcAttr.trim() === '') return;
      const rawUrl = el.src || (el.href && typeof el.href === 'object' ? el.href.baseVal : el.href) || null;
      let url = rawUrl;
      if (rawUrl) {
        try {
          url = new URL(rawUrl, location.href).href;
        } catch (e) {
          url = rawUrl;
        }
      }
      const isOurDomain = url && url.startsWith(location.origin);
      if (url === location.href || !isOurDomain) {
        return;
      }
      report({
        type: 'resource',
        message: 'Resource load error: ' + el.tagName.toLowerCase() + (url ? ' ' + url : ''),
        source: url
      });
      return;
    }

    const error = event.error;
    report({
      type: 'error',
      message: (error && error.message) || event.message || 'Unknown error',
      stack: error && error.stack ? String(error.stack) : null,
      source: event.filename || null,
      line: event.lineno,
      column: event.colno
    });
  }

  function onUnhandledRejection(event) {
    const reason = event.reason;
    if (reason instanceof Error) {
      report({
        type: 'unhandledrejection',
        message: reason.message || 'Unhandled promise rejection',
        stack: reason.stack ? String(reason.stack) : null
      });
    } else {
      report({
        type: 'unhandledrejection',
        message: 'Unhandled promise rejection: ' + stringifyValue(reason)
      });
    }
  }

  function wrappedConsoleError() {
    const args = Array.prototype.slice.call(arguments);
    originalConsoleError.apply(console, args);
    if (clarityIdentifyDepth > 0) return;
    report({
      type: 'console.error',
      message: args.map(stringifyValue).join(' '),
      stack: getFirstErrorStack(args)
    });
  }

  function initLogger() {
    if (initialized) return;

    initialized = true;

    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('pagehide', flushPendingReports);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        flushPendingReports();
      }
    });

    originalConsoleError = console.error;
    console.error = wrappedConsoleError;
  }
  initLogger()
})()